import { Injectable } from '@angular/core';
import { ADMIN_ROUTE_PERMISSIONS } from '../permissions/admin-route-permissions';
import { decodeJwtPayload } from '../utils/jwt.util';

const WILDCARD = '*';
const LOGIN_ROUTE = '/login';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  /** Stored login permissions first, then the JWT `permissions` claim. Anything malformed yields []. */
  public permissions(): string[] {
    const stored = this.parseList(this.readStored());
    if (stored) return stored;

    const claims = decodeJwtPayload<{ permissions?: unknown }>(localStorage.getItem('token'));
    return this.parseList(claims?.permissions) ?? [];
  }

  public has(permission: string): boolean {
    const granted = this.permissions();
    return granted.includes(WILDCARD) || granted.includes(permission);
  }

  public hasAny(permissions: readonly string[]): boolean {
    return permissions.some((permission) => this.has(permission));
  }

  /** Organization id from the JWT `organizationId` claim; null for platform users and old tokens without the claim. */
  public organizationId(): number | null {
    const claims = decodeJwtPayload<{ organizationId?: unknown }>(localStorage.getItem('token'));
    const id = claims?.organizationId;
    return typeof id === 'number' ? id : null;
  }

  public hasOrganization(): boolean {
    return this.organizationId() !== null;
  }

  /** First admin route the user can open, or /login when there is none. */
  public landingRoute(): string {
    const match = ADMIN_ROUTE_PERMISSIONS.find(
      (entry) => this.has(entry.permission) && (!entry.requiresOrganization || this.hasOrganization())
    );
    return match ? match.route : LOGIN_ROUTE;
  }

  private readStored(): unknown {
    try {
      const raw = localStorage.getItem('permissions');
      return raw === null ? undefined : JSON.parse(raw);
    } catch {
      return undefined;
    }
  }

  private parseList(value: unknown): string[] | null {
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : null;
  }
}
